import { z } from 'zod'
import { groupMuteDurationValues } from './group'

export const placeTypeValues = ['shared', 'public', 'event'] as const
export const arrivalMuteDurationValues = groupMuteDurationValues
export type ArrivalMuteDuration = (typeof arrivalMuteDurationValues)[number]
export const placeMemberRoleValues = ['owner', 'admin', 'member'] as const
export const placeMemberStatusValues = ['active', 'pending'] as const

export const createPlaceSchema = z.object({
  name: z.string().min(1).max(80),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  radiusM: z.number().int().min(50).max(500).optional(),
  sensitive: z.boolean().optional(),
})
export type CreatePlaceInput = z.infer<typeof createPlaceSchema>

export const updatePlaceSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  radiusM: z.number().int().min(50).max(500).optional(),
  sensitive: z.boolean().optional(),
  allowMemberInvites: z.boolean().optional(),
  pinned: z.boolean().optional(),
  /** Caller's own per-place arrival-alert opt-in. */
  notifyOnArrival: z.boolean().optional(),
  /** Silence this spot's arrival alerts for a window without giving up the opt-in. */
  arrivalMute: z
    .object({
      muted: z.boolean(),
      /** Applies when muting; omitted means an indefinite mute. */
      duration: z.enum(arrivalMuteDurationValues).optional(),
    })
    .optional(),
})
export type UpdatePlaceInput = z.infer<typeof updatePlaceSchema>

export const savePlaceSchema = z.object({ circleId: z.string().uuid() })
export type SavePlaceInput = z.infer<typeof savePlaceSchema>

export const directAddMemberSchema = z.object({ userId: z.string().uuid() })

/** Owner promotes a member to admin (so they can add others too), or demotes back. */
export const updateMemberRoleSchema = z.object({ role: z.enum(['admin', 'member']) })

export const joinPlaceSchema = z.object({ code: z.string().min(1).max(40) })
export type JoinPlaceInput = z.infer<typeof joinPlaceSchema>

export const createInviteSchema = z.object({
  expiresInDays: z.number().int().min(1).max(90).optional(),
  maxUses: z.number().int().min(1).max(500).optional(),
})

export const placeDtoSchema = z.object({
  id: z.string().uuid(),
  type: z.enum(placeTypeValues),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  /** Street and city resolved from the coordinate on the server. Empty when the
   * coordinate has no address, or while the backfill has not reached the spot. */
  locationStreet: z.string().optional(),
  locationCity: z.string().optional(),
  radiusM: z.number().int(),
  ownerId: z.string().uuid().nullable(),
  listingId: z.string().uuid().nullable(),
  /** Custom header image; null = show the map. */
  backgroundUrl: z.string().nullable(),
  /** Dominant colour of the header image, for squircle tint + text-contrast. Null with no header. */
  backgroundColor: z.string().nullable(),
  /** The requesting user's role at this place (null for a saved public venue). */
  myRole: z.enum(placeMemberRoleValues).nullable(),
  pinned: z.boolean(),
  /** Caller's own arrival-alert opt-in for this place. */
  myNotifyOnArrival: z.boolean(),
  /** When the caller's arrival-alert mute expires; far-future for an indefinite mute, null when unmuted. */
  myArrivalMutedUntil: z.string().nullable().optional(),
  /** When true, ordinary members (not just owner/admin) can invite or add others. */
  allowMemberInvites: z.boolean(),
  memberCount: z.number().int(),
  createdAt: z.string(),
  /** Set only for type 'event': the source event and its live window. */
  eventId: z.string().uuid().nullable().optional(),
  eventStartTime: z.string().optional(),
  eventEndTime: z.string().optional(),
  /** Event and public venue places: anonymized count of everyone currently present. */
  presentTotal: z.number().int().optional(),
  /**
   * False on event places surfaced only because a friend is there (discovery):
   * the viewer can look but is not eligible for presence — the device must not
   * fence or heartbeat it. Absent means true.
   */
  canBePresent: z.boolean().optional(),
})
export type PlaceDto = z.infer<typeof placeDtoSchema>

export const placeMemberFriendStateValues = [
  'self',
  'friends',
  'requested',
  'incoming',
  'none',
] as const
export type PlaceMemberFriendState = (typeof placeMemberFriendStateValues)[number]

export const placeMemberDtoSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: z.enum(placeMemberRoleValues),
  status: z.enum(placeMemberStatusValues),
  joinedAt: z.string(),
  /**
   * How the viewer relates to this member: `requested` is an outgoing request,
   * `incoming` one waiting on the viewer. Drives the add-friend affordance —
   * co-membership alone no longer reveals presence, so this is how the two
   * become visible to each other.
   */
  friendState: z.enum(placeMemberFriendStateValues).optional(),
  /** Accepted friends the viewer shares with this member. Absent means unknown, not zero. */
  mutualFriendCount: z.number().int().nonnegative().optional(),
  /** Joined on the web without an account, so never offered as a friend. Absent means unknown. */
  isGuest: z.boolean().optional(),
})
export type PlaceMemberDto = z.infer<typeof placeMemberDtoSchema>

/** Optional across the board: absent means the whole roster, unfiltered. */
export const placeMembersQuerySchema = z.object({
  q: z.string().trim().min(1).max(80).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
})
export type PlaceMembersQuery = z.infer<typeof placeMembersQuerySchema>

export const placeInviteDtoSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  url: z.string(),
  expiresAt: z.string().nullable(),
  maxUses: z.number().int().nullable(),
  useCount: z.number().int(),
  revoked: z.boolean(),
})
export type PlaceInviteDto = z.infer<typeof placeInviteDtoSchema>

export const createPingSchema = z.object({ text: z.string().min(1).max(140) })
export type CreatePingInput = z.infer<typeof createPingSchema>

export const respondPingSchema = z.object({ coming: z.boolean() })

/** A spontaneous spot ping with its live yes/no tally and the viewer's own answer. */
export const spotPingDtoSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  authorId: z.string().uuid(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  yesCount: z.number().int(),
  noCount: z.number().int(),
  myResponse: z.boolean().nullable(),
})
export type SpotPingDto = z.infer<typeof spotPingDtoSchema>

export const spotPingResponseSchema = z.object({ ping: spotPingDtoSchema })
export const spotPingsResponseSchema = z.object({ pings: z.array(spotPingDtoSchema) })
export type SpotPingsResponse = z.infer<typeof spotPingsResponseSchema>
export type SpotPingResponse = z.infer<typeof spotPingResponseSchema>

export const placesResponseSchema = z.object({ places: z.array(placeDtoSchema) })
export const placeResponseSchema = z.object({ place: placeDtoSchema })

/**
 * Result of redeeming a spot invite. `invitedBy` names whoever created the code
 * when they are neither the joiner nor already their friend, so the app can offer
 * to connect the two — it never sends a friend request on its own.
 */
export const placeJoinResponseSchema = z.object({
  place: placeDtoSchema,
  /** What redeeming the code did: a new active membership, a new one awaiting the
   * owner's approval, or nothing because the caller already belonged. */
  outcome: z.enum(['joined', 'pending', 'already_member']),
  invitedBy: z
    .object({
      id: z.string().uuid(),
      displayName: z.string().nullable(),
      avatarUrl: z.string().nullable(),
    })
    .nullable(),
})
export const placeMembersResponseSchema = z.object({
  members: z.array(placeMemberDtoSchema),
  /** Matching rows before paging. Present only when the caller paged or searched. */
  total: z.number().int().optional(),
})

export const placeInvitePreviewStatusValues = [
  'valid',
  'expired',
  'revoked',
  'exhausted',
  'unknown',
] as const
export type PlaceInvitePreviewStatus = (typeof placeInvitePreviewStatusValues)[number]

/**
 * Pre-authentication view of a spot behind an invite code. Only `valid` carries
 * `spot`; every other status deliberately carries nothing, so a forwarded or
 * guessed code reveals no spot at all. `presentCount` is 0 below two people —
 * one person plus a member list identifies them. Nobody present is ever named:
 * the only face here is the inviter's.
 */
export const placeInvitePreviewResponseSchema = z.object({
  status: z.enum(placeInvitePreviewStatusValues),
  spot: z
    .object({
      name: z.string(),
      memberCount: z.number().int(),
      presentCount: z.number().int(),
      backgroundUrl: z.string().nullable(),
      backgroundColor: z.string().nullable(),
    })
    .optional(),
  invitedBy: z
    .object({ displayName: z.string().nullable(), avatarUrl: z.string().nullable() })
    .nullable()
    .optional(),
})
export type PlaceInvitePreviewResponse = z.infer<typeof placeInvitePreviewResponseSchema>
export const placeInviteResponseSchema = z.object({ invite: placeInviteDtoSchema })

export type PlacesResponse = z.infer<typeof placesResponseSchema>
export type PlaceResponse = z.infer<typeof placeResponseSchema>
export type PlaceJoinResponse = z.infer<typeof placeJoinResponseSchema>
export type PlaceMembersResponse = z.infer<typeof placeMembersResponseSchema>
export type PlaceInviteResponse = z.infer<typeof placeInviteResponseSchema>
